import React, { useCallback, useState } from 'react';
import type { NodeProps } from 'reactflow';
import { Handle, Position } from 'reactflow';
import { Box, Stack, Typography, IconButton, Chip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import StorageIcon from '@mui/icons-material/Storage';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import type { CanvasNodeData } from '../types';
import { CANVAS_GROUP_HEADER_HEIGHT } from '../layoutConstants';

/**
 * SourceOriginGroupNode — container node that groups source tables by their
 * connector / upload origin. When collapsed, shows a compact summary with
 * aggregate mapping stats. When expanded, child TableGroupNode instances are
 * rendered inside it by React Flow's built-in parent-child layout.
 */
export const SourceOriginGroupNode: React.FC<NodeProps<CanvasNodeData>> = React.memo(({ id, data, selected }) => {
  const tone = '#7c3aed'; // Purple to distinguish from source (blue) and target (teal)
  const isConnector = data.originType === 'connector';
  const Icon = isConnector ? StorageIcon : UploadFileIcon;
  const label = data.originLabel || data.label || 'Source';
  const tableCount = data.childTableCount ?? 0;
  const mappedFields = data.childMappedFields ?? 0;
  const totalFields = data.childTotalFields ?? 0;
  const expanded = data.originExpanded ?? true;
  const [hovered, setHovered] = useState(false);

  const handleToggle = useCallback(() => {
    data.onToggleOriginExpand?.();
  }, [data.onToggleOriginExpand]);

  return (
    <Box
      data-id={id}
      data-kind="sourceOriginGroup"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        width: '100%',
        height: '100%',
        borderRadius: '8px',
        border: `2px solid ${alpha(tone, selected ? 0.5 : 0.2)}`,
        background: `linear-gradient(145deg, ${alpha(tone, 0.03)} 0%, ${alpha(tone, 0.06)} 100%)`,
        boxShadow: selected
          ? `0 0 0 1px ${tone}, 0 8px 24px rgba(15, 23, 42, 0.1)`
          : `0 0 0 1px ${alpha(tone, 0.1)}, 0 4px 16px rgba(15, 23, 42, 0.06)`,
        position: 'relative',
        pointerEvents: 'none',
        overflow: 'hidden',
        transition: 'all 0.2s ease-in-out',
      }}
    >
      {/* Bundle edge handle for collapsed state */}
      <Handle
        type="source"
        position={Position.Right}
        id="source"
        isConnectable={false}
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: tone,
          border: '2px solid #fff',
          right: -4,
          top: CANVAS_GROUP_HEADER_HEIGHT / 2,
          opacity: expanded ? 0 : 1,
        }}
      />

      {/* Header */}
      <Box
        sx={{
          height: CANVAS_GROUP_HEADER_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          px: 2,
          gap: 1,
          pointerEvents: 'auto',
          cursor: 'pointer',
          '&:hover': { backgroundColor: alpha(tone, 0.06) },
        }}
        onClick={handleToggle}
      >
        <Icon sx={{ fontSize: 18, color: tone, opacity: 0.8 }} />
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            color: '#1e293b',
            fontSize: '0.8rem',
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </Typography>
        <Chip
          label={`${tableCount} table${tableCount !== 1 ? 's' : ''}`}
          size="small"
          sx={{
            height: 20,
            fontSize: '0.65rem',
            fontWeight: 600,
            backgroundColor: alpha(tone, 0.1),
            color: tone,
          }}
        />
        {!expanded && totalFields > 0 && (
          <Chip
            label={`${mappedFields}/${totalFields} fields`}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.65rem',
              fontWeight: 500,
              backgroundColor: alpha('#2563eb', 0.08),
              color: '#2563eb',
            }}
          />
        )}
        <IconButton size="small" sx={{ pointerEvents: 'auto', p: 0.25 }}>
          {expanded ? (
            <ExpandLessIcon sx={{ fontSize: 16, color: '#64748b' }} />
          ) : (
            <ExpandMoreIcon sx={{ fontSize: 16, color: '#64748b' }} />
          )}
        </IconButton>
      </Box>

      {/* Footer: "Add table" action for connector origins */}
      {expanded && isConnector && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 8,
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'center',
            pointerEvents: 'auto',
          }}
        >
          <Box
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              data.onAddTableFromOrigin?.();
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1.5,
              py: 0.5,
              borderRadius: '6px',
              border: `1px dashed ${alpha(tone, 0.3)}`,
              cursor: 'pointer',
              transition: 'all 0.15s',
              '&:hover': {
                backgroundColor: alpha(tone, 0.06),
                borderColor: alpha(tone, 0.5),
              },
            }}
          >
            <Typography variant="caption" sx={{ color: alpha(tone, 0.7), fontSize: '0.7rem' }}>
              ➕ Add table from this source
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
});

SourceOriginGroupNode.displayName = 'SourceOriginGroupNode';
